// Consolidated opportunity service combining opportunitiesApiRTK and opportunityAnalyticsApi
import { baseApi } from "../api/baseApi";
import {
  Opportunity,
  WorkOpportunity,
  OpportunityDetail,
  OpportunityDetailDto,
  CreateOpportunityRequest,
  OpportunityUpdateRequest,
  CreateDetailRequest,
  OpportunityProductFields,
  OpportunityProductProperties,
  OpportunityProductRequest,
  OpportunityStatus,
} from "../types/opportunity.types";
import { OpportunityStatusString } from "../../lib/utils/statusConverter";

// Re-export types for backward compatibility
export type {
  Opportunity,
  WorkOpportunity,
  OpportunityDetail,
  OpportunityDetailDto,
  CreateOpportunityRequest,
  OpportunityUpdateRequest,
  CreateDetailRequest,
  OpportunityProductFields,
  OpportunityProductProperties,
  OpportunityProductRequest,
};
export { OpportunityStatus };

// Analytics types
interface ProductAnalytics {
  ProductType: string;
  Count: number;
  AverageValue: number;
  PricePoints: PricePointDistribution[];
}

interface PricePointDistribution {
  PriceRange: number;
  Count: number;
  TotalValue: number;
  Percentage: number;
}

interface MonthlyMetrics {
  Month: string;
  NewOpportunities: number;
  ClosedWon: number;
  ClosedLost: number;
  TotalValue: number;
  WinRate: number;
  AverageDealSize: number;
}

interface TimelineAnalytics {
  MonthlyData: MonthlyMetrics[];
  AverageSalesCycleDays: number;
}

// Updated to handle both new and old response formats
export interface PipelineStageData {
  // For backward compatibility, allow both string names and numeric steps
  Status: number | OpportunityStatusString;
  Count: number;
  // Support both field names (Value from old API, TotalValue from new API)
  Value?: number;
  TotalValue?: number;
  ConversionRate: number;
}

export interface OpportunityAnalyticsResponse {
  PipelineAnalytics: PipelineStageData[];
  ProductAnalytics: ProductAnalytics[];
  TimelineAnalytics: TimelineAnalytics;
}

interface TopProduct {
  ProductName: string;
  Count: number;
  TotalValue: number;
}

// Define a service using a base URL and expected endpoints
export const opportunityService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Original opportunity CRUD endpoints from opportunitiesApiRTK
    getCustomerOpportunities: builder.query<WorkOpportunity[], number>({
      query: (customerNo) => ({
        url: `/v3/crm/customer/${customerNo}/opportunities`,
        method: "GET",
      }),
      providesTags: ["Opportunities"],
    }),

    getOpportunity: builder.query<WorkOpportunity, number>({
      query: (opportunityNo) => ({
        url: `/v3/crm/${opportunityNo}`,
        method: "GET",
      }),
      providesTags: ["Opportunities"],
    }),

    createOpportunity: builder.mutation<Opportunity, CreateOpportunityRequest>({
      query: (body) => ({
        url: "/v3/crm/opportunity",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Opportunities"],
    }),

    updateOpportunity: builder.mutation<
      Opportunity,
      { opportunityNo: number; body: OpportunityUpdateRequest }
    >({
      query: ({ opportunityNo, body }) => ({
        url: `/v3/crm/opportunity/${opportunityNo}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Opportunities"],
    }),

    addOpportunityDetail: builder.mutation<
      OpportunityDetail,
      CreateDetailRequest
    >({
      query: (body) => ({
        url: `/v3/crm/opportunity/${body.OpportunityNo}/detail`,
        method: "POST",
        body: {
          SalesProductNo: body.SalesProductNo,
          OpportunityDetailqty: body.OpportunityDetailqty,
          OpportunityDetailPrice: body.OpportunityDetailPrice,
          OpportunityDetailTotalAmount: body.OpportunityDetailTotalAmount,
        },
      }),
      invalidatesTags: ["Opportunities"],
    }),

    getMyOpportunities: builder.query<WorkOpportunity[], void>({
      query: () => ({
        url: "/v3/crm/opportunities/my",
        method: "GET",
      }),
      providesTags: ["Opportunities"],
    }),

    getCompanyOpportunities: builder.query<
      WorkOpportunity[],
      { startDate?: string | Date | null; endDate?: string | Date | null }
    >({
      query: (params) => {
        let url = "/v3/crm/opportunities/company";
        const queryParams = new URLSearchParams();

        if (params?.startDate) {
          const formattedStartDate =
            params.startDate instanceof Date
              ? params.startDate.toISOString()
              : params.startDate;
          queryParams.append("startDate", formattedStartDate);
        }

        if (params?.endDate) {
          const formattedEndDate =
            params.endDate instanceof Date
              ? params.endDate.toISOString()
              : params.endDate;
          queryParams.append("endDate", formattedEndDate);
        }

        const queryString = queryParams.toString();
        if (queryString) {
          url += `?${queryString}`;
        }

        return {
          url,
          method: "GET",
        };
      },
      providesTags: ["Opportunities"],
    }),

    // Analytics endpoints from opportunityAnalyticsApi
    getOpportunityAnalytics: builder.query<
      OpportunityAnalyticsResponse,
      { startDate?: string; endDate?: string }
    >({
      query: ({ startDate, endDate }) => {
        let url = "/v1/analytics/opportunities";
        const params = new URLSearchParams();

        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);

        if (params.toString()) {
          url += `?${params.toString()}`;
        }

        return { url };
      },
      // Transform the response to normalize field names if needed
      transformResponse: (response: OpportunityAnalyticsResponse) => {
        // Make sure PipelineAnalytics always has both Value and TotalValue
        const normalizedPipeline = response.PipelineAnalytics.map(stage => ({
          ...stage,
          // If TotalValue exists but Value doesn't, copy TotalValue to Value
          Value: stage.Value ?? stage.TotalValue ?? 0,
          // If Value exists but TotalValue doesn't, copy Value to TotalValue
          TotalValue: stage.TotalValue ?? stage.Value ?? 0
        }));

        return {
          ...response,
          PipelineAnalytics: normalizedPipeline
        };
      },
      providesTags: ["Opportunities"],
    }),

    getSupplierDistribution: builder.query<Record<string, number>, void>({
      query: () => ({
        url: "/v1/analytics/opportunities/suppliers",
      }),
      providesTags: ["Opportunities"],
    }),

    getMonthlyValue: builder.query<Record<string, number>, void>({
      query: () => ({
        url: "/v1/analytics/opportunities/monthly-value",
      }),
      providesTags: ["Opportunities"],
    }),

    getTopProducts: builder.query<TopProduct[], number>({
      query: (limit = 10) => ({
        url: `/v1/analytics/opportunities/top-products?top=${limit}`,
      }),
      providesTags: ["Opportunities"],
    }),
  }),
});

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const {
  // Original opportunity CRUD hooks
  useGetCompanyOpportunitiesQuery,
  useGetCustomerOpportunitiesQuery,
  useGetOpportunityQuery,
  useCreateOpportunityMutation,
  useUpdateOpportunityMutation,
  useAddOpportunityDetailMutation,
  useGetMyOpportunitiesQuery,
  // Analytics hooks
  useGetOpportunityAnalyticsQuery,
  useGetSupplierDistributionQuery,
  useGetMonthlyValueQuery,
  useGetTopProductsQuery,
} = opportunityService;

// Export the service for use in other files if needed
export default opportunityService;